/** Shared 429 handling: Retry-After parsing, cooldown state, and UI events. */

import { parseApiErrorBody } from "./parseApiError";
import { isSimulateInflightErrorSlug } from "./simulateQuotaToasts";

function isDemoEntryAnalysisInflightSlug(slug: string | undefined): boolean {
  return String(slug || "").toLowerCase().includes("entry_analysis.inflight");
}

export type RateLimitActionKey =
  | "report-generate"
  | "simulate"
  | "csv-import"
  | "custom-csv-preview"
  | "custom-csv-inspect"
  | "pine-parse"
  | "api";

export const RATE_LIMIT_EVENT = "tessera:rate-limited";

export type RateLimitEventDetail = {
  actionKey: RateLimitActionKey;
  retryAfterSeconds: number;
  endpoint?: string;
  message?: string;
  errorSlug?: string;
};

const cooldownUntilByAction = new Map<RateLimitActionKey, number>();

export function parseRetryAfterSeconds(response: Response, fallbackSeconds = 60): number {
  const raw = response.headers.get("Retry-After");
  if (!raw) return fallbackSeconds;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber >= 0) return Math.ceil(asNumber);
  const asDate = Date.parse(raw);
  if (!Number.isNaN(asDate)) {
    return Math.max(1, Math.ceil((asDate - Date.now()) / 1000));
  }
  return fallbackSeconds;
}

export function rateLimitToastMessage(retryAfterSeconds: number): string {
  return `Too many requests. Try again in ${retryAfterSeconds}s.`;
}

export function actionKeyFromEndpoint(endpoint: string): RateLimitActionKey {
  const path = endpoint.split("?")[0] ?? endpoint;
  if (path.includes("/reports/generate")) return "report-generate";
  if (path.includes("/simulate-exit-placement") || path.includes("/trade-policy/simulate")) return "simulate";
  if (path.includes("custom-csv/inspect")) return "custom-csv-inspect";
  if (path.includes("custom-csv/preview")) return "custom-csv-preview";
  if (path.includes("csv-import") || path.includes("custom-csv/import")) return "csv-import";
  if (path.includes("/parse-preview")) return "pine-parse";
  return "api";
}

export function setRateLimitCooldown(actionKey: RateLimitActionKey, retryAfterSeconds: number): void {
  const until = Date.now() + retryAfterSeconds * 1000;
  const prev = cooldownUntilByAction.get(actionKey) ?? 0;
  cooldownUntilByAction.set(actionKey, Math.max(prev, until));
  cooldownUntilByAction.set("api", Math.max(cooldownUntilByAction.get("api") ?? 0, until));
}

export function getRateLimitRemainingSeconds(actionKey: RateLimitActionKey): number {
  const until = cooldownUntilByAction.get(actionKey) ?? 0;
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

export function isRateLimitCoolingDown(actionKey: RateLimitActionKey): boolean {
  return getRateLimitRemainingSeconds(actionKey) > 0;
}

export function notifyRateLimited(detail: RateLimitEventDetail): void {
  if (!isSimulateInflightErrorSlug(detail.errorSlug) && detail.retryAfterSeconds > 0) {
    setRateLimitCooldown(detail.actionKey, detail.retryAfterSeconds);
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<RateLimitEventDetail>(RATE_LIMIT_EVENT, { detail }));
}

export function handleRateLimitedResponse(response: Response, endpoint: string): boolean {
  if (response.status !== 429) return false;

  const headerRetry = parseRetryAfterSeconds(response);
  const actionKey = actionKeyFromEndpoint(endpoint);

  const notify = (retryAfterSeconds: number, message?: string, errorSlug?: string) => {
    notifyRateLimited({
      actionKey,
      retryAfterSeconds,
      endpoint,
      message,
      errorSlug,
    });
  };

  void response
    .clone()
    .json()
    .then((body) => {
      const parsed = parseApiErrorBody(
        body && typeof body === "object" ? (body as Record<string, unknown>) : {},
        rateLimitToastMessage(headerRetry)
      );
      const retryAfterSeconds = parsed.retryAfterSeconds ?? headerRetry;
      if (isSimulateInflightErrorSlug(parsed.slug) || isDemoEntryAnalysisInflightSlug(parsed.slug)) {
        notifyRateLimited({
          actionKey,
          retryAfterSeconds: 0,
          endpoint,
          message: parsed.message,
          errorSlug: parsed.slug,
        });
        return;
      }
      notify(retryAfterSeconds, parsed.message, parsed.slug);
    })
    .catch(() => {
      notify(headerRetry);
    });

  return true;
}
